import { useSettings, useRoom, useSession } from "@turnaroundfactor/react";
import adapt from "@turnaroundfactor/common";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import Avatar from "../../components/Avatar";
import logger from "../../utils/logging";
import updateARState from "../../utils/updateARState";
import { useTranslationStore } from "../../hooks/useTranslation";
import { useShallow } from "zustand/react/shallow";

const DEFAULT_VOLUME = 0.5;

function PeerUser({ name, type, uuid }) {
	const vidRef = useRef(null);
	const node = useRef(null);

	const { broadcast, users } = useRoom();

	const peer = useMemo(
		(_) => {
			return adapt.room?.connections.find((m) => m.uuid === uuid);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[uuid, users]
	);

	const videoID = useMemo((_) => `peerUser-${uuid}`, [uuid]);
	const [videoEnabled, setVideoEnabled] = useState(peer.videoEnabled);
	const [audioEnabled, setAudioEnabled] = useState(peer.audioEnabled);
	const [connectionDegraded, setConnectionDegraded] = useState(false);
	const [screen, setScreen] = useState(null);
	const [orientation, setOrientation] = useState("landscape");
	const { user: currentUser } = useSession();
	const [online, setOnlineState] = useState(true);
	const [mediaActive, setMediaActive] = useState(peer.connected);

	const { permissions = [] } = currentUser || {};

	const { settings, update: updateSettings } = useSettings();
	const { pinned, translationEnabled, translationAudioEnabled } = settings;

	const { addSession, closeSession } = useTranslationStore(
		useShallow((store) => ({
			addSession: store.addSession,
			closeSession: store.closeSession,
		}))
	);

	function kickPeer(event) {
		event?.preventDefault();
		// eslint-disable-next-line no-restricted-globals
		if (confirm(`Kick ${name}?`)) {
			if (adapt.room) adapt.room.kick(uuid);
		}
	}

	function mutePeer(type = "audio") {
		broadcast("MUTE", {
			uuid,
			type,
			source: { name: currentUser.name, uuid: currentUser.id },
		});
	}


	function trackHandler(track) {
		const player = vidRef.current;
		if (!player || !track) return;

		let stream = player.srcObject;
		const newTrack = track;

		if (!stream || !(stream instanceof MediaStream)) {
			stream = new MediaStream();
			player.srcObject = stream;
		}

		if(track.kind === "video") {
			stream.getVideoTracks().forEach((t) => stream.removeTrack(t));
		} else if(track.kind === "audio") {
			stream.getAudioTracks().forEach((t) => stream.removeTrack(t));
		}

		stream.addTrack(newTrack);
		
		if (newTrack.enabled === false) {
			newTrack.enabled = true;
		}
	}

	useEffect(() => {
		const player = vidRef.current;

		function setupPlayer() {
			player.muted = false;
			player.volume = DEFAULT_VOLUME;
			
			if (peer?.tracks.length > 0) {
				player.srcObject = new MediaStream(peer.tracks);
			}
		}

		if (player) setupPlayer();
	}, [vidRef]);

	useEffect(() => {
		const player = vidRef.current;
		if (translationEnabled && translationAudioEnabled) {
			if (player) player.volume = 0.1;
		} else {
			if (player) player.volume = DEFAULT_VOLUME;
		}
	}, [translationEnabled, translationAudioEnabled]);

	/**
	 * Effect run when stream/peer is setup to handle incoming media and map it to the 
	 * video element's srcObject. Also maps local state watchers and speaking/translatinon events
	 * to update the UI accordingly. Cleans up listeners and media on unmount.
	 * 
	 */
	useEffect(() => {
		let rendered = true;
		
		peer.on("TRACK", trackHandler);
		peer.on("CONNECTION_DEGRADED", (_) => {
			logger.debug(`Connection to ${peer.name} degraded.`);
			if (rendered === true) setConnectionDegraded(true);
		});

		if (type === "OnSiteUser") {
			peer.on("CHANGE_SCREEN", (s) => setScreen(s));
		}

		peer.on("CONNECTION_ACTIVE", (_) => setConnectionDegraded(false));
		peer.on("LOCAL_STATE", ({ audio, video, orientation, translate }) => {

			console.log(`Received local state from ${peer.name}`, {
				audio,
				video,
				orientation,
				translate,
			});
			if (video !== undefined) setVideoEnabled(video);
			if (audio !== undefined) setAudioEnabled(audio);
			if (orientation !== undefined) setOrientation(orientation);

			if (translate) {
				console.log("Translate not null starting local translation session from local state trigger", { translate });
				addSession({ uuid, language: translate });
			} else if (translate !== undefined) closeSession(uuid);

			setOnlineState(true);
			setConnectionDegraded(false);
		});

		peer.on("SPEAKING", (data) => {
			const div = node.current;
			if (data.speaking && audioEnabled === true) {
				div?.classList.add("-speaking");
			} else {
				div?.classList.remove("-speaking");
			}
		});

		peer.on("TRANSLATION", (data) => {
			console.log("TRANSLATION DATA", data);
		});

		peer.on("TRANSLATION_AUDIO", (data) => {
			console.log("TRANSLATION AUDIO", data);
		});

		peer.on("PRESENCE", ({ online }) => {
			logger.debug(`Presence state changed for ${peer.name}. Online: ${online}`);
			setConnectionDegraded(!online);
			setOnlineState(online);
		});

		peer.on('MEDIA_ACTIVE', () => {
			console.log('Media active event received for', peer.name);
			setMediaActive(true)
			peer.connected = true;
		});


		setTimeout(() => {
			if (!rendered) return;
			console.log('Activating media for', peer.name);
			setMediaActive(true);
		}, 15000);

		return () => {
			updateARState(uuid, false);
			closeSession(uuid);
			rendered = false;
			peer.off();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const ratio = "16by9";
	const isPinned = pinned === peer.uuid;

	return (
		<div
			className={`peer-user ${videoEnabled === false ? "-muted" : ""}`}
			ref={node}
			data-type={type}
			data-media-active={mediaActive}
			data-connected={peer.connected}
		>
			<figure className={` video ${orientation}`} data-testid="video" data-peer-id={uuid}>
				<video id={videoID} style={mediaActive ? {} : { opacity: 0 }} autoPlay playsInline ref={vidRef}></video>
				<figcaption>
					{name}
					{screen && (
						<>
							<br />
							viewing: ({screen})
						</>
					)}
				</figcaption>
			</figure>
			{(videoEnabled === false || online === false || mediaActive === false) && (
				<div data-testid="avatar" className="peer-avatar d-flex flex-column align-items-center">
					<Avatar user={{ name }} size={75} />
				</div>
			)}
			<Dropdown className="peer-options">
				<Dropdown.Toggle variant="link text-white">
					<i className="fas fa-fw fa-ellipsis-v"></i>
				</Dropdown.Toggle>
				<Dropdown.Menu className="rounded">
					<Dropdown.Header className="pb-0">{name}</Dropdown.Header>
					<Dropdown.Divider />
					<Dropdown.Item as="button" disabled={audioEnabled === false} onClick={(e) => mutePeer("audio")}>
						Mute Audio
					</Dropdown.Item>
					<Dropdown.Item as="button" disabled={videoEnabled === false} onClick={(e) => mutePeer("video")}>
						Mute Video
					</Dropdown.Item>
					<Dropdown.Item as="button" onClick={(e) => updateSettings({ pinned: isPinned ? null : peer.uuid })}>
						{isPinned ? "Un-Pin" : "Pin User"}
					</Dropdown.Item>
					{permissions.includes("room_kick") && (
						<Dropdown.Item as="button" onClick={kickPeer}>
							Kick
						</Dropdown.Item>
					)}
				</Dropdown.Menu>
			</Dropdown>
			<div className="peer-status-indicators d-flex align-items-center">
				{audioEnabled === false && (
					<i data-testid="audioIcon" className="fa fa-fw fa-microphone-slash text-danger rounded-circle" />
				)}
				{(connectionDegraded === true || online === false) && (
					<i data-testid="connectionIcon" className="fa fa-fw fa-plug text-danger rounded-circle" />
				)}
			</div>
		</div>
	);
}

export default React.memo(PeerUser);
